import { typographyV2 } from '@toggle/design-system';
import styled, { css, keyframes } from 'styled-components';

export const Container = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: var(--surface-default);
`;

export const Card = styled.div`
  width: 100%;
  max-width: 480px;
  padding: 32px;
  border-radius: 12px;
  background: var(--surface-soft);
  border: 1px solid var(--border-soft);
`;

export const Title = styled.h1`
  ${typographyV2.titleLg};
  margin-bottom: 8px;
  color: var(--text-default);
`;

export const Subtitle = styled.p`
  ${typographyV2.bodySm};
  color: var(--text-soft);
  margin-bottom: 24px;
`;

export const DomainList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DomainItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 8px;
  background: var(--surface-default);
  border: 1px solid var(--border-soft);
`;

export const DomainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DomainName = styled.span`
  ${typographyV2.bodyBase};
  color: var(--text-default);
  font-family: monospace;
`;

export const TypeBadge = styled.span`
  ${typographyV2.bodyXs};
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--surface-hover);
  color: var(--text-soft);
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

export const StatusIndicator = styled.span<{
  $status: 'checking' | 'success' | 'error';
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  ${typographyV2.bodySm};

  ${({ $status }) => {
    switch ($status) {
      case 'checking':
        return css`
          color: var(--text-soft);
          animation: ${pulse} 1.5s ease-in-out infinite;
        `;
      case 'success':
        return css`
          color: var(--text-success);
        `;
      case 'error':
        return css`
          color: var(--text-critical);
        `;
    }
  }}
`;

export const StatusDot = styled.span<{
  $status: 'checking' | 'success' | 'error';
}>`
  width: 8px;
  height: 8px;
  border-radius: 50%;

  ${({ $status }) => {
    switch ($status) {
      case 'checking':
        return css`
          background: var(--icon-soft);
        `;
      case 'success':
        return css`
          background: var(--icon-success);
        `;
      case 'error':
        return css`
          background: var(--icon-critical);
        `;
    }
  }}
`;

export const SummaryBar = styled.div<{ $allSuccess: boolean }>`
  margin-top: 24px;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${typographyV2.bodyBase};

  ${({ $allSuccess }) =>
    $allSuccess
      ? css`
          background: var(--surface-success);
          color: var(--text-success);
        `
      : css`
          background: var(--surface-critical);
          color: var(--text-critical);
        `}
`;

export const RefreshButton = styled.button`
  margin-top: 16px;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  background: var(--action-primary-default);
  color: var(--text-on-primary);
  ${typographyV2.bodyBase};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--action-primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
